import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import '../ui/alert.js';
import '@radix-ui/react-avatar';
import 'date-fns-tz';
import { cn } from '../../utils/formatters.js';
import '@radix-ui/react-label';
import '../ui/button.js';
import '@radix-ui/react-progress';
import '@clerk/clerk-react';
import { List, ChevronDown, Check, Users, Plus, Pencil, Trash2 } from 'lucide-react';
import { useState, useMemo } from 'react';
import '../../lib/toast.js';
import 'react-router-dom';
import { Button } from '../page-layout/Button.js';
import '../../contexts/AppContext.js';
import '../../contexts/OrganizationContext.js';
import 'axios';
import '@tanstack/react-query';
import '../page-layout/FormPageLayout.js';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from '../ui/command.js';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover.js';
import '../form/RichTextEditor.js';
import '@radix-ui/react-switch';
import '@radix-ui/react-dialog';
import './CrudDetailView.js';
import './CrudFormView.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import '@dnd-kit/core';
import '@dnd-kit/sortable';
import '@dnd-kit/utilities';
import '../ui/badge.js';
import 'react-day-picker';
import '../ui/chart.js';
import '@radix-ui/react-checkbox';
import '@radix-ui/react-collapsible';
import 'vaul';
import '@radix-ui/react-dropdown-menu';
import '../ui/radio-group.js';
import '@radix-ui/react-select';
import '../ui/time-input.js';
import '@radix-ui/react-separator';
import '../ui/sidebar.js';
import 'react-toastify';
import '@radix-ui/react-tabs';
import '@radix-ui/react-tooltip';

const DEFAULT_VISIBLE = 10;
function SavedListSelector({ entityNamePlural, views, selectedListId, onSelectList, onSaveNew, onEdit, onDelete, }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [showAll, setShowAll] = useState(false);
    const selectedView = views.find(v => v.id === selectedListId);
    const personalViews = useMemo(() => views.filter(v => v.scope === 'personal'), [views]);
    const sharedViews = useMemo(() => views.filter(v => v.scope === 'shared'), [views]);
    // Filter by search
    const filteredPersonal = useMemo(() => {
        if (!search)
            return personalViews;
        const q = search.toLowerCase();
        return personalViews.filter(v => v.name.toLowerCase().includes(q));
    }, [personalViews, search]);
    const filteredShared = useMemo(() => {
        if (!search)
            return sharedViews;
        const q = search.toLowerCase();
        return sharedViews.filter(v => v.name.toLowerCase().includes(q));
    }, [sharedViews, search]);
    const allFiltered = [...filteredPersonal, ...filteredShared];
    const totalCount = allFiltered.length;
    const visibleCount = showAll ? totalCount : Math.min(totalCount, DEFAULT_VISIBLE);
    // Slice views for display respecting the limit
    let personalToShow = filteredPersonal;
    let sharedToShow = filteredShared;
    if (!showAll && totalCount > DEFAULT_VISIBLE) {
        if (filteredPersonal.length >= DEFAULT_VISIBLE) {
            personalToShow = filteredPersonal.slice(0, DEFAULT_VISIBLE);
            sharedToShow = [];
        }
        else {
            personalToShow = filteredPersonal;
            sharedToShow = filteredShared.slice(0, DEFAULT_VISIBLE - filteredPersonal.length);
        }
    }
    const hasMore = totalCount > visibleCount;
    const handleSelect = (view) => {
        onSelectList(view);
        setOpen(false);
        setSearch('');
        setShowAll(false);
    };
    return (jsxs(Popover, { open: open, onOpenChange: (val) => {
            setOpen(val);
            if (!val) {
                setSearch('');
                setShowAll(false);
            }
        }, children: [jsx(PopoverTrigger, { asChild: true, children: jsxs(Button, { variant: "outline", className: "h-9 gap-2 shrink-0", children: [jsx(List, { className: "h-4 w-4" }), jsx("span", { className: "hidden sm:inline max-w-[160px] truncate", children: selectedView?.name || `All ${entityNamePlural}` }), jsx(ChevronDown, { className: "h-3.5 w-3.5 opacity-50" })] }) }), jsx(PopoverContent, { className: "w-[280px] p-0", align: "start", children: jsxs(Command, { shouldFilter: false, children: [jsx(CommandInput, { placeholder: "Search saved views...", value: search, onValueChange: setSearch }), jsxs(CommandList, { children: [jsx(CommandEmpty, { children: "No saved views found." }), jsx(CommandGroup, { children: jsxs(CommandItem, { onSelect: () => handleSelect(null), className: "flex items-center gap-2", children: [jsx(Check, { className: cn('h-4 w-4', !selectedListId ? 'opacity-100' : 'opacity-0') }), jsx(List, { className: "h-4 w-4" }), "All ", entityNamePlural] }) }), personalToShow.length > 0 && (jsxs(Fragment, { children: [jsx(CommandSeparator, {}), jsx(CommandGroup, { heading: "Personal", children: personalToShow.map(view => (jsx(ViewItem, { view: view, isSelected: selectedListId === view.id, onSelect: () => handleSelect(view), onEdit: () => { setOpen(false); onEdit(view); }, onDelete: () => { setOpen(false); onDelete(view); } }, view.id))) })] })), sharedToShow.length > 0 && (jsxs(Fragment, { children: [jsx(CommandSeparator, {}), jsx(CommandGroup, { heading: jsxs("span", { className: "flex items-center gap-1", children: [jsx(Users, { className: "h-3 w-3" }), "Shared"] }), children: sharedToShow.map(view => (jsx(ViewItem, { view: view, isSelected: selectedListId === view.id, onSelect: () => handleSelect(view), onEdit: () => { setOpen(false); onEdit(view); }, onDelete: () => { setOpen(false); onDelete(view); } }, view.id))) })] })), hasMore && (jsx(CommandGroup, { children: jsxs(CommandItem, { onSelect: () => setShowAll(true), className: "justify-center text-muted-foreground text-xs", children: ["Show ", totalCount - visibleCount, " more..."] }) })), jsx(CommandSeparator, {}), jsx(CommandGroup, { children: jsxs(CommandItem, { onSelect: () => { setOpen(false); onSaveNew(); }, className: "flex items-center gap-2", children: [jsx(Plus, { className: "h-4 w-4" }), "Save Current View"] }) })] })] }) })] }));
}
function ViewItem({ view, isSelected, onSelect, onEdit, onDelete, }) {
    return (jsxs(CommandItem, { onSelect: onSelect, className: "flex items-center justify-between group", children: [jsxs("div", { className: "flex items-center gap-2 min-w-0", children: [jsx(Check, { className: cn('h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0') }), jsx("span", { className: "truncate", children: view.name })] }), jsxs("div", { className: "flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-data-[selected=true]:opacity-100 transition-opacity shrink-0", children: [jsx("button", { type: "button", className: "p-0.5 hover:text-foreground text-muted-foreground", onClick: e => {
                            e.stopPropagation();
                            onEdit();
                        }, children: jsx(Pencil, { className: "h-3 w-3" }) }), jsx("button", { type: "button", className: "p-0.5 hover:text-destructive text-muted-foreground", onClick: e => {
                            e.stopPropagation();
                            onDelete();
                        }, children: jsx(Trash2, { className: "h-3 w-3" }) })] })] }));
}

export { SavedListSelector };
//# sourceMappingURL=SavedListSelector.js.map
